/**
 * UI.js — Shared primitive components
 *
 * All reusable, stateless (or minimally stateful) UI atoms live here.
 * Import from this file rather than duplicating markup across pages.
 *
 * Exports:
 *   AlertProvider  — context wrapper that renders the global toast overlay
 *   showAlert()    — imperative helper to trigger a toast from anywhere
 *   Modal          — overlay dialog with header, body, optional footer
 *   StatusPill     — colour-coded attendance status badge
 *   Avatar         — worker photo or initials fallback
 *   Dropdown       — button + floating menu with outside-click dismissal
 *   Tabs           — horizontal tab bar
 *   Table          — data table with optional custom cell renderers
 *   FormGroup      — label + input wrapper for consistent form layout
 */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { formatStatus, STATUS_COLORS } from '../utils/helpers';

// ── Alert toast ───────────────────────────────────────────────────────────────
//
// Pattern: module-level mutable reference to the state setter.
// This allows `showAlert()` to be called from non-component code (e.g. API
// error handlers in page files) without needing prop drilling or a second
// context. The setter is only valid after AlertProvider mounts.

let alertTimer = null;
let setGlobalAlert = null;

const ALERT_ICONS = {
  success: 'check_circle',
  error:   'cancel',
  warning: 'warning',
  info:    'info',
};

/**
 * Wraps the app and renders a self-dismissing toast when showAlert() is called.
 * Must be placed near the root so it is always mounted.
 */
export function AlertProvider({ children }) {
  const [alert, setAlert] = useState(null);
  // Wire the imperative helper to this component's state setter
  setGlobalAlert = setAlert;

  useEffect(() => {
    if (alert) {
      clearTimeout(alertTimer);
      alertTimer = setTimeout(() => setAlert(null), 3000);
    }
  }, [alert]);

  return (
    <>
      {children}
      {alert && (
        <div className={`alert ${alert.type}`}>
          <span className="material-symbols-rounded alert-icon">
            {ALERT_ICONS[alert.type] || 'info'}
          </span>
          {alert.message}
        </div>
      )}
    </>
  );
}

/**
 * Show a toast notification. Can be called from outside React components.
 *
 * @param {string} message
 * @param {'success'|'error'|'warning'|'info'} [type='success']
 */
export function showAlert(message, type = 'success') {
  setGlobalAlert?.({ message, type });
}

// ── Modal ─────────────────────────────────────────────────────────────────────

/**
 * Accessible overlay dialog.
 *
 * Closes on:
 *   - Escape key press
 *   - Click on the semi-transparent backdrop (mouseDown on overlay, not modal)
 *   - The ✕ icon button
 *
 * Uses `onMouseDown` (not `onClick`) on the overlay to avoid closing when the
 * user starts a drag inside the modal and releases outside it.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   title: string,
 *   children: React.ReactNode,
 *   footer?: React.ReactNode,
 *   size?: 'modal-sm' | '' | 'modal-lg'
 * }} props
 */
export function Modal({ open, onClose, title, children, footer, size = '' }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${size}`} onMouseDown={e => e.stopPropagation()}>
        <div className="modal-header">
          <span>{title}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────

/**
 * Colour-coded badge for an attendance status value.
 * CSS classes (e.g. `.status-pill.present`) are defined in index.css.
 *
 * @param {{ status: string }} props
 */
export function StatusPill({ status }) {
  return (
    <span className={`status-pill ${status}`}>{formatStatus(status)}</span>
  );
}

// ── Worker avatar ─────────────────────────────────────────────────────────────

const BASE = process.env.REACT_APP_DIRECTUS_URL || 'https://staff.innomalous.com';

/**
 * Build a sized, authenticated Directus asset URL.
 *
 * `photo` is either a UUID string (unexpanded relation) or an object with an `id`
 * field (expanded via *.*.*). Both map to the same /assets/{uuid} path.
 *
 * Auth: dev mode appends the stored access token as a query param because cross-origin
 * image requests from localhost can't send the httpOnly session cookie. In production
 * both subdomains share innomalous.com so the cookie is forwarded automatically.
 *
 * @param {string|{id:string}|null} photo
 * @param {number} px - desired square dimension in CSS pixels
 */
function resolveAssetUrl(photo, px) {
  if (!photo) return null;
  const uuid = typeof photo === 'string' ? photo : photo.id;
  if (!uuid) return null;
  const url = `${BASE}/assets/${uuid}?width=${px}&height=${px}&fit=cover`;
  if (process.env.NODE_ENV === 'development') {
    const token = localStorage.getItem('dev_access_token');
    if (token) return `${url}&access_token=${token}`;
  }
  return url;
}

/**
 * Displays a worker's photo if available, otherwise renders initials.
 * Image is requested at 2× the display size for retina clarity.
 * Initials are the first letters of the first and last name words.
 *
 * @param {{
 *   worker: { name?: string, photo?: string | { id: string } },
 *   size?: number  — pixel dimension (default 40)
 * }} props
 */
export function Avatar({ worker, size = 40 }) {
  const initials = (worker?.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const src = resolveAssetUrl(worker?.photo, size * 2);

  return (
    <div className="worker-avatar" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {src ? <img src={src} alt={worker.name} /> : initials}
    </div>
  );
}

// ── Dropdown menu ─────────────────────────────────────────────────────────────

/**
 * A trigger element + floating menu with outside-click dismissal.
 *
 * @param {{
 *   trigger: React.ReactNode,
 *   items: Array<{
 *     label: string,
 *     icon?: React.ElementType,
 *     onClick?: () => void,
 *     danger?: boolean,
 *     disabled?: boolean,
 *     hidden?: boolean,
 *   }>
 * }} props
 */
export function Dropdown({ trigger, items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const trigRef = useRef(null);
  const menuRef = useRef(null);

  function openMenu() {
    const rect = trigRef.current.getBoundingClientRect();
    // Align menu's right edge with trigger's right edge, drop below trigger
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpen(o => !o);
  }

  // Close when clicking outside both the trigger and the floating menu
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!menuRef.current?.contains(e.target) && !trigRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="dropdown-trigger" ref={trigRef}>
      <span onClick={openMenu}>{trigger}</span>
      {open && pos && ReactDOM.createPortal(
        <div ref={menuRef} className="dropdown-menu" style={{ top: pos.top, right: pos.right }}>
          {items.map((item, i) =>
            item.hidden ? null : (
              <div
                key={i}
                className={`dropdown-item ${item.danger ? 'danger' : ''}`}
                style={item.disabled ? { opacity: 0.4, pointerEvents: 'none' } : {}}
                onClick={() => { setOpen(false); item.onClick?.(); }}
              >
                {item.icon && <item.icon size={14} />}
                {item.label}
              </div>
            )
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

/**
 * Horizontal tab bar — controlled component, caller owns active state.
 *
 * @param {{
 *   tabs: Array<{ id: string, label: string }>,
 *   active: string,
 *   onChange: (id: string) => void
 * }} props
 */
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="tabs">
      {tabs.map(tab => (
        <button
          key={tab.id}
          className={`tab-btn ${active === tab.id ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ── Simple table ──────────────────────────────────────────────────────────────

/**
 * Data table with optional per-column custom cell renderers.
 * Renders an empty state message when `data` is empty or undefined.
 *
 * Column definition:
 *   key     — property name on each row object
 *   label   — column header text
 *   render? — (value, row) => ReactNode  for custom cell content
 *
 * @param {{
 *   columns: Array<{ key: string, label: string, render?: (value, row) => React.ReactNode }>,
 *   data: object[],
 *   emptyText?: string
 * }} props
 */
export function Table({ columns, data, emptyText = 'No data' }) {
  if (!data?.length) return <div className="empty-state">{emptyText}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              {columns.map(c => (
                <td key={c.key}>
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Form helpers ──────────────────────────────────────────────────────────────

/**
 * Wraps a form field with a label. Provides consistent vertical spacing and
 * label typography. Children should be a single <input>, <select>, or similar.
 *
 * @param {{ label: string, children: React.ReactNode }} props
 */
export function FormGroup({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}
